/**
 * The client portal map's aircraft and vehicles (Diurnal parity 03-053; MB's decisions 64, 74, 75, 345, 765;
 * docs/specs/portal/client-area.md §11; 04-security-and-tenancy.md §3). A client sees the organisation's aircraft and
 * field vehicles assigned to the project (Teslyn Field's "Show to this project's client", `tracker_assignment`), each
 * fix only inside its assignment window, from the positions the cloud tracker poller keeps for 48 hours. The database
 * hands over only those fixes (`portal_tracking_positions`, `portal_tracking_assets` of
 * `20261027012000_portal_map.sql`): never another project's or an unassigned aircraft, never public air traffic
 * (decision 765), never SOS or lost tracking (decision 64), never a tracker's device id or account.
 *
 * Here the stored fixes become the map with Teslyn Field's own rules (`hubTracking`: one icon per aircraft, the trail,
 * take-off, flight time and landing from `flightStatus`), then every tracker state is taken off: no "SOS", no "Lost
 * tracking", no grey "No recent position" (that is lost tracking in other words); the last position simply says when
 * it was. The source's colours stay (V2Track's blues and the rest).
 */
import type { AircraftKind, AircraftPosition, TrailPoint } from './aircraft-layer.js';
import { flightStatusLabel, flightStatusWords } from './flight-status.js';
import { hubTracking, type HubPositionRow } from './ops-hub.js';
import { formatOffset, utcToLocalMs } from './time-model.js';
import {
  DEFAULT_TRACKING_SETTINGS,
  FLEET_PROVIDERS,
  aircraftColours,
  type AircraftColours,
  type FleetProviderId,
  type TrackerAsset,
} from './tracker.js';

/** The trail choices of the client's map (decision 75: up to 48 hours), as Teslyn Field's history depth. */
export const PORTAL_TRAIL_HOURS = [1, 2, 4, 6, 12, 24, 48] as const;
export const PORTAL_TRAIL_DEFAULT = 12;
/**
 * The client portal map's replay (N16; parity 03-031, decision 75): the last 48 hours of the aircraft and vehicles
 * shown to the client, the most `portal_tracking_positions` gives (`PORTAL_TRAIL_HOURS`' last), whatever trail the
 * map shows.
 */
export const PORTAL_REPLAY_HOURS = 48;
export const PORTAL_REPLAY_NOTE =
  'Replaying the last 48 hours: each aircraft and vehicle shown to you at its last position at that time.';
/** The map asks again every minute: the cloud poller stores new fixes about once a minute (decision 419). */
export const PORTAL_TRACKING_REFRESH_MS = 60_000;

/** One aircraft or vehicle assignment the client may see (`portal_tracking_assets`). */
export interface PortalAssetRow {
  readonly asset_id: string;
  readonly provider: string;
  readonly label: string;
  readonly registration: string;
  readonly kind: string;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
}

/** One fix the client may see (`portal_tracking_positions`). */
export interface PortalPositionRow {
  readonly asset_id: string;
  readonly source: string;
  readonly lat: number;
  readonly lon: number;
  readonly alt_ft: number | null;
  readonly speed_kt: number | null;
  readonly heading_deg: number | null;
  readonly fix_time: string;
  readonly airborne: boolean;
}

/** One line of the list under the client's map. */
export interface PortalTrackingRow {
  readonly id: string;
  readonly label: string;
  readonly kind: AircraftKind;
  readonly airborne: boolean;
  /** "In the air" or "On the ground" at the last position; a vehicle "Vehicle". */
  readonly where: string;
  /** "3,512 ft · 136 kt" (an aircraft in the air), else ''. */
  readonly figures: string;
  /** "Last position 14:22 (UTC+8)", with the day when it is not today. */
  readonly lastFix: string;
  /** "T/O 14:22 · FT 3.8h · LDG 18:08", or '' (decision 64). */
  readonly flight: string;
  /** The same in words for a screen reader. */
  readonly flightWords: string;
}

export interface PortalTracking {
  /** No tracker state on any of them (decision 64). */
  readonly aircraft: readonly AircraftPosition[];
  readonly trails: ReadonlyMap<string, readonly TrailPoint[]>;
  readonly colours: ReadonlyMap<string, AircraftColours>;
  readonly rows: readonly PortalTrackingRow[];
}

const KINDS: readonly AircraftKind[] = ['fixed_wing', 'helicopter', 'ground'];
const kindOf = (k: string): AircraftKind =>
  KINDS.includes(k as AircraftKind) ? (k as AircraftKind) : 'fixed_wing';
const isFleet = (p: string): p is FleetProviderId => (FLEET_PROVIDERS as readonly string[]).includes(p);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const local = (utcMs: number, offsetH: number): Date => new Date(utcToLocalMs(utcMs, offsetH));
/** "1 Oct 2026" in the offset given. */
export function portalDate(iso: string, offsetH: number): string {
  const d = local(Date.parse(iso), offsetH);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
const hhmm = (utcMs: number, offsetH: number): string => {
  const d = local(utcMs, offsetH);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

/** An assignment's window in words: "from 1 Oct 2026 to 30 Nov 2026", "from 1 Oct 2026", "until …", or ''. */
export function portalWindowWords(a: Pick<PortalAssetRow, 'starts_at' | 'ends_at'>, offsetH: number): string {
  const from = a.starts_at ? `from ${portalDate(a.starts_at, offsetH)}` : '';
  const to = a.ends_at ? `${a.starts_at ? 'to' : 'until'} ${portalDate(a.ends_at, offsetH)}` : '';
  return [from, to].filter(Boolean).join(' ');
}

/** An assigned asset's name: "VH-JOZ", or "Survey ute (vehicle)". */
export function portalAssetName(a: Pick<PortalAssetRow, 'label' | 'registration' | 'kind'>): string {
  const name = a.label || a.registration || 'An aircraft';
  return kindOf(a.kind) === 'ground' ? `${name} (vehicle)` : name;
}

/**
 * What the client can see, in plain words (04-security-and-tenancy.md §3): "You can see VH-JOZ on F26048 from 1 Oct
 * 2026 to 30 Nov 2026." One sentence per assignment, in the order given.
 */
export function portalEntitlementWords(
  assets: readonly PortalAssetRow[],
  projectNumber: string,
  offsetH: number,
): string[] {
  return assets.map((a) => {
    const window = portalWindowWords(a, offsetH);
    return `You can see ${portalAssetName(a)} on ${projectNumber}${window ? ` ${window}` : ''}.`;
  });
}

/**
 * The client's map from the fixes the database handed over: Teslyn Field's one icon per aircraft, trails and flight
 * status, with every tracker state removed.
 */
export function portalTracking(input: {
  readonly assets: readonly PortalAssetRow[];
  readonly rows: readonly PortalPositionRow[];
  readonly nowMs: number;
  /** The viewer's UTC offset in hours, for the times. */
  readonly offsetH: number;
}): PortalTracking {
  const byAsset = new Map<string, PortalAssetRow>();
  for (const a of input.assets)
    if (isFleet(a.provider) && !byAsset.has(a.asset_id)) byAsset.set(a.asset_id, a);
  // the asset id stands in for the device: the client is never given a tracker's device id or account
  const assets: TrackerAsset[] = [...byAsset.values()].map((a) => ({
    id: a.asset_id,
    tenant_id: '',
    provider: a.provider as FleetProviderId,
    account_label: '',
    device_id: a.asset_id,
    label: a.label,
    registration: a.registration,
    icao_hex: '',
    kind: kindOf(a.kind),
    colour: 'accent',
    fleet_group: '',
    aircraft_id: null,
    hidden: false,
    deleted_at: null,
  }));
  const rows: HubPositionRow[] = input.rows
    .filter((r) => byAsset.has(r.asset_id) && isFleet(r.source))
    .map((r) => {
      const a = byAsset.get(r.asset_id)!;
      return {
        asset_id: r.asset_id,
        source: r.source,
        account_label: '',
        source_id: r.asset_id,
        label: a.label || a.registration,
        kind: kindOf(a.kind),
        registration: a.registration,
        lat: r.lat,
        lon: r.lon,
        alt_ft: r.alt_ft,
        speed_kt: r.speed_kt,
        heading_deg: r.heading_deg,
        fix_time: r.fix_time,
        airborne: r.airborne,
      };
    });
  const t = hubTracking({
    assets,
    rows,
    sos: new Set(),
    settings: DEFAULT_TRACKING_SETTINGS,
    nowMs: input.nowMs,
    offsetH: input.offsetH,
  });
  const lastAirborne = new Map<string, boolean>();
  for (const r of rows) lastAirborne.set(`asset:${r.asset_id}`, r.airborne);
  const plainOf = (a: AircraftPosition): AircraftPosition => {
    // decision 64: no SOS, lost tracking or stale state, no link type, no tracker name or group
    const { state: _state, satellite: _satellite, rfds: _rfds, fire: _fire, ...plain } = a;
    void _state;
    void _satellite;
    void _rfds;
    void _fire;
    return {
      ...plain,
      source: '',
      group: '',
      airborne: a.kind !== 'ground' && (lastAirborne.get(a.id) ?? a.airborne),
      fleet: true,
    };
  };
  // the tracker's colours (its own blues for V2Track), worked out with no state, so never the grey of lost tracking
  const colours = aircraftColours(t.aircraft.map((a) => ({ ...plainOf(a), source: a.source })));
  const aircraft = t.aircraft.map(plainOf);
  const zone = `UTC${formatOffset(input.offsetH)}`;
  const today = portalDate(new Date(input.nowMs).toISOString(), input.offsetH);
  const list: PortalTrackingRow[] = aircraft
    .map((a) => {
      const fixMs = Date.parse(a.time);
      const day = portalDate(a.time, input.offsetH);
      const flight = a.flight ?? null;
      const figures =
        a.airborne && a.kind !== 'ground'
          ? [
              a.alt_ft !== null ? `${Math.round(a.alt_ft).toLocaleString('en-AU')} ft` : '',
              a.speed_kt !== null ? `${Math.round(a.speed_kt)} kt` : '',
            ]
              .filter(Boolean)
              .join(' · ')
          : '';
      return {
        id: a.id,
        label: a.label,
        kind: a.kind,
        airborne: a.airborne,
        where: a.kind === 'ground' ? 'Vehicle' : a.airborne ? 'In the air' : 'On the ground',
        figures,
        lastFix: `Last position ${hhmm(fixMs, input.offsetH)} (${zone})${day === today ? '' : `, ${day}`}`,
        flight: flight ? flightStatusLabel(flight, input.offsetH) : '',
        flightWords: flight ? flightStatusWords(flight, input.offsetH) : '',
      };
    })
    .sort((x, y) => x.label.localeCompare(y.label, 'en-AU'));
  return { aircraft, trails: t.trails, colours, rows: list };
}
